import { useState } from "react";
import { 
  FiChevronUp, 
  FiChevronDown, 
  FiChevronLeft, 
  FiChevronRight,
  FiZoomIn,
  FiZoomOut,
  FiX,
  FiHome,
} from "react-icons/fi";
import authAxios from "../../../api/authAxios";

/**
 * PTZControls
 * ========================================
 * Quick PTZ controls overlay
 * - Directional pad
 * - Zoom in/out
 * - Preset positions
 * - Home position
 */
export default function PTZControls({ camera, onClose }) {
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState([
    { id: 1, name: "Entrance" },
    { id: 2, name: "Parking" },
    { id: 3, name: "Overview" },
  ]);

  // PTZ Command
  const sendPTZCommand = async (command, params = {}) => {
    setLoading(true);
    try {
      await authAxios.post(`/cameras/${camera.id}/ptz/`, {
        command,
        ...params,
      });
    } catch (err) {
      console.error("PTZ command failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="absolute inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-20">
      <div className="relative bg-neutral-800 rounded-2xl p-6 shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-lg hover:bg-neutral-700 text-gray-400 hover:text-white transition"
        >
          <FiX size={20} />
        </button>

        {/* Title */}
        <div className="text-center mb-6">
          <h3 className="text-white font-semibold text-lg">PTZ Controls</h3>
          <p className="text-gray-400 text-sm">{camera.name}</p>
        </div>

        <div className="flex gap-6">
          {/* Directional Pad */}
          <div className="flex flex-col items-center">
            <div className="text-white text-xs font-medium mb-2">Direction</div>
            <div className="grid grid-cols-3 gap-2">
              {/* Top Row */}
              <div />
              <button
                onClick={() => sendPTZCommand("move", { direction: "up" })}
                className="p-3 rounded-lg bg-neutral-700 hover:bg-blue-600 text-white transition-all active:scale-95"
                disabled={loading}
              >
                <FiChevronUp size={20} />
              </button>
              <div />

              {/* Middle Row */}
              <button
                onClick={() => sendPTZCommand("move", { direction: "left" })}
                className="p-3 rounded-lg bg-neutral-700 hover:bg-blue-600 text-white transition-all active:scale-95"
                disabled={loading}
              >
                <FiChevronLeft size={20} />
              </button>
              <button
                onClick={() => sendPTZCommand("home")}
                className="p-3 rounded-lg bg-neutral-700 hover:bg-green-600 text-white transition-all active:scale-95"
                disabled={loading}
                title="Home position"
              >
                <FiHome size={20} />
              </button>
              <button
                onClick={() => sendPTZCommand("move", { direction: "right" })}
                className="p-3 rounded-lg bg-neutral-700 hover:bg-blue-600 text-white transition-all active:scale-95"
                disabled={loading}
              >
                <FiChevronRight size={20} />
              </button>

              {/* Bottom Row */}
              <div />
              <button
                onClick={() => sendPTZCommand("move", { direction: "down" })}
                className="p-3 rounded-lg bg-neutral-700 hover:bg-blue-600 text-white transition-all active:scale-95"
                disabled={loading}
              >
                <FiChevronDown size={20} />
              </button>
              <div />
            </div>
          </div>

          {/* Zoom Controls */}
          <div className="flex flex-col items-center">
            <div className="text-white text-xs font-medium mb-2">Zoom</div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => sendPTZCommand("zoom", { direction: "in" })}
                className="p-3 rounded-lg bg-neutral-700 hover:bg-blue-600 text-white transition-all active:scale-95"
                disabled={loading}
              >
                <FiZoomIn size={20} />
              </button>
              <button
                onClick={() => sendPTZCommand("zoom", { direction: "out" })}
                className="p-3 rounded-lg bg-neutral-700 hover:bg-blue-600 text-white transition-all active:scale-95"
                disabled={loading}
              >
                <FiZoomOut size={20} />
              </button>
            </div>
          </div>

          {/* Presets */}
          <div className="flex flex-col">
            <div className="text-white text-xs font-medium mb-2">Presets</div>
            <div className="space-y-2">
              {presets.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => sendPTZCommand("goto_preset", { preset_id: preset.id })}
                  className="px-4 py-2 rounded-lg bg-neutral-700 hover:bg-purple-600 text-white text-sm transition-all active:scale-95 min-w-[120px]"
                  disabled={loading}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
          </div>
        )}
      </div>
    </div>
  );
}
